import type { QueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/types";
import { FAMILY_PAYMENT_ALLOCATION_QUERY_KEY } from "./family-payment-allocation";
import { OPEN_BALANCE_QUERY_KEY } from "./open-balance";
import { clearParentPortalBalanceSettling } from "./parent-portal-balance-settling";
import { userBalanceDetailsQueryKey } from "./user-balance-details-query";

/** Parent-portal queries that must refresh after a family payment. */
const USER_PAYMENT_QUERY_PREFIXES = (userId: number) =>
  [
    [QueryKeys.Balance, OPEN_BALANCE_QUERY_KEY, userId],
    [QueryKeys.Balance, FAMILY_PAYMENT_ALLOCATION_QUERY_KEY, userId],
    [QueryKeys.Balance, "user-statements", userId],
    userBalanceDetailsQueryKey(userId),
    [QueryKeys.Payments, userId],
  ] as const;

type RefetchScope = "active" | "all";

async function refetchUserPaymentQueries(
  queryClient: QueryClient,
  userId: number,
  scope: RefetchScope,
): Promise<void> {
  const refetchType = scope === "all" ? "all" : "active";

  await Promise.all(
    USER_PAYMENT_QUERY_PREFIXES(userId).map((queryKey) =>
      queryClient.refetchQueries({
        queryKey,
        type: refetchType,
      }),
    ),
  );
}

async function markAdminRosterQueriesStale(
  queryClient: QueryClient,
  userId: number,
  studentId?: number,
): Promise<void> {
  const tasks = [
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.UserGet, userId],
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.UserStudentsGet, userId],
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.UsersScrollGet],
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.BalanceSummary],
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.WidgetData],
      refetchType: "none",
    }),
  ];

  if (studentId != null && studentId > 0) {
    tasks.push(
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.Students, studentId],
        refetchType: "none",
      }),
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.Balance, "student-statements", studentId],
        refetchType: "none",
      }),
    );
  }

  await Promise.all(tasks);
}

/** Parent Pay Now: only refresh this family's cached balance views. */
export async function invalidateParentPortalBalanceAfterPayment(
  queryClient: QueryClient,
  userId: number,
): Promise<void> {
  await refetchUserPaymentQueries(queryClient, userId, "all");
}

/**
 * Release returns before the balance is posted. Refresh once more after 10 seconds.
 * The success handler already refreshes immediately.
 */
export function scheduleParentPortalBalancePaymentRefetch(
  queryClient: QueryClient,
  userId: number,
): void {
  window.setTimeout(() => {
    void refetchUserPaymentQueries(queryClient, userId, "all").finally(() => {
      clearParentPortalBalanceSettling(userId);
    });
  }, 10_000);
}

/** Admin charge: refresh family payment data plus mark admin views stale. */
export async function invalidateAdminBalanceAfterPayment(
  queryClient: QueryClient,
  {
    userId,
    studentId,
  }: {
    userId: number;
    studentId?: number;
  },
): Promise<void> {
  await refetchUserPaymentQueries(queryClient, userId, "active");

  if (studentId != null && studentId > 0) {
    await queryClient.refetchQueries({
      queryKey: [QueryKeys.Balance, "student-statements", studentId],
      type: "active",
    });
  }

  await markAdminRosterQueriesStale(queryClient, userId, studentId);
}

export function scheduleAdminBalancePaymentRefetch(
  queryClient: QueryClient,
  userId: number,
  studentId?: number,
): void {
  window.setTimeout(() => {
    void refetchUserPaymentQueries(queryClient, userId, "active");
    if (studentId != null && studentId > 0) {
      void queryClient.refetchQueries({
        queryKey: [QueryKeys.Balance, "student-statements", studentId],
        type: "active",
      });
    }
  }, 10_000);
}

/** @deprecated Use portal-specific helpers instead. */
export async function invalidateMemberBalancePaymentQueries(
  queryClient: QueryClient,
  userId: number,
): Promise<void> {
  await invalidateParentPortalBalanceAfterPayment(queryClient, userId);
}

/** @deprecated Use portal-specific helpers instead. */
export async function refetchMemberBalancePaymentQueries(
  queryClient: QueryClient,
  userId: number,
  scope: RefetchScope = "active",
): Promise<void> {
  await refetchUserPaymentQueries(queryClient, userId, scope);
}

/** @deprecated Use scheduleParentPortalBalancePaymentRefetch instead. */
export function scheduleMemberBalancePaymentRefetch(
  queryClient: QueryClient,
  userId: number,
): void {
  scheduleParentPortalBalancePaymentRefetch(queryClient, userId);
}
